<!--
  Joining a group by its code.

  A group can be formed before OR after playback starts, so this screen has two
  jobs: if the room is already watching something, get out of the way and go
  straight to it; if it is not, hold the door open and say who is here.
-->
<script lang="ts">
  import { enterRoomWhenLoaded } from '$lib/sync/room-entry';
  import RoomInviteSheet from '$lib/social/RoomInviteSheet.svelte';
  import { me, invitationRoomSnapshot } from '$lib/social/api';
  import { inviteError } from '$lib/social/invite-errors';
  import { watchRoomAccess } from '$lib/social/room-access';
  import { untrack } from 'svelte';
  import Button from '$lib/ui/Button.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import Spinner from '$lib/ui/Spinner.svelte';
  import type { RoomSnapshot } from '$lib/ui/api';
  import { ago, count } from '$lib/ui/format';
  import { go, nav, setCurrentRoom, watchHref } from '$lib/ui/nav.svelte';

  const roomId = $derived(nav.route.name === 'room' ? nav.route.roomId : '');

  let snapshot = $state<RoomSnapshot | null>(null);
  let error = $state('');
  let inviteOpen = $state(false);
  let authorized = $state(false);
  let denied = $state(false);
  $effect(() => {
    void roomId;
    denied = false;
    return watchRoomAccess(() => import('$lib/player/sync-shared'), message => {
      denied = true;
      error = message;
      setCurrentRoom(null);
    });
  });

  // Opening a room link puts this tab in that group, so every play link built
  // from here on carries it — including the ones on other screens. Without this,
  // "Find something" was a one-way door out of the room.
  $effect(() => {
    const id = roomId;
    if (id) untrack(() => setCurrentRoom(id));
  });

  /*
   * WAITING IN A ROOM IS BEING IN IT.
   *
   * This screen used to poll `/api/rooms/:id` over REST and never open the
   * socket, so a person sitting here was in the room only in their own head:
   * two friends who both opened the link each saw "Nobody is in tulip-421 yet"
   * while looking at each other's names in a chat window. It also meant they
   * were invisible to a friend's presence list and their evening was never
   * written to their room history — the visit is recorded when a member JOINS,
   * and nobody had.
   *
   * The engine is the tab's one singleton, and `joinRoom` is idempotent, so the
   * watch screen this hands over to inherits the membership instead of making a
   * second one. A member who is here and not playing anything reports `idle`,
   * which is exactly the state the hub already ignores when deciding whether to
   * hold a room for somebody — waiting repairs the slow, never the absent.
   *
   * WAITING IS BEING PRESENT, and this screen says so out loud. A tab that had
   * just come off a watch screen has withdrawn its assertion, so without this line
   * two friends opening a room link before anyone pressed play would each create a
   * room the server deleted in the same breath — the defect this screen exists to
   * fix (`I3`). It does NOT withdraw on unmount: "Find something" is deliberately
   * not a way out of the room, and leaving THE PLAYER is what the owner's rule is
   * about.
   *
   * ── AND WHOEVER GETS HERE FIRST OWNS THE ROOM ──────────────────────────────
   *
   * A room is created by the join that names it (`RoomHub.join`), so the first
   * person to open the link — from here or from the player — is its owner, and its
   * lifetime is their attendance from then on. That is a real consequence of
   * asserting presence here and it is the accepted one: what matters is that the
   * room ends when THAT person leaves, and that everybody else arriving and
   * leaving neither lengthens nor shortens it. If the owner is the friend who
   * opened the link first and then wandered off, the room ends and this screen
   * says so below rather than spinning.
   *
   * Dynamically imported for the same reason the player and the call dock are:
   * a browser that never opens a room link never downloads the sync engine.
   */
  const syncModule = import.meta.glob('/src/lib/player/sync-shared.ts');
  $effect(() => {
    const id = roomId;
    if (!id || !authorized || denied) return;
    const load = syncModule['/src/lib/player/sync-shared.ts'];
    if (!load) return;
    return enterRoomWhenLoaded(id, load);
  });

  $effect(() => {
    const id = roomId;
    if (!id) return;
    authorized = false;
    snapshot = null;
    error = '';
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async (): Promise<void> => {
      if (denied) return;
      try {
        await me(AbortSignal.timeout(15_000));
        const found = await invitationRoomSnapshot(id, AbortSignal.timeout(15_000));
        if (stopped || denied) return;
        snapshot = found;
        authorized = !found.ended;
        error = '';

        // A ROOM THAT HAS ENDED IS A FINAL ANSWER. Its code is held against reuse, so
        // nothing this poll could learn later would change the screen — going on asking
        // every three seconds would only be a spinner wearing a different hat.
        //
        // And this tab is no longer in a group. Without that line the dead code stays in
        // `currentRoom`, every play link built afterwards still carries `?room=<dead>`,
        // and the next film opens into a room that can only refuse it.
        if (found.ended) {
          setCurrentRoom(null);
          return;
        }

        const source = found.room?.source;
        if (source) {
          // Already watching: adopt it. `replace` so Back does not bounce here.
          go(
            watchHref({
              kind: source.content.kind,
              imdbId: source.content.imdbId,
              season: source.content.season ?? null,
              episode: source.content.episode ?? null,
              roomId: id
            }),
            { replace: true }
          );
          return;
        }
      } catch (err) {
        if (stopped) return;
        error = inviteError(err);
        if ([401, 403, 404, 410].includes((err as { status?: number }).status ?? 0)) {
          denied = true;
          setCurrentRoom(null);
          return;
        }
      }
      if (!stopped) timer = setTimeout(() => void poll(), 3000);
    };

    untrack(() => void poll());
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  });

  const room = $derived(snapshot?.room ?? null);

  function share(): void { inviteOpen = true; }
</script>

<svelte:head><title>Room {roomId} — Jovii</title></svelte:head>

{#if inviteOpen}<RoomInviteSheet bind:open={inviteOpen} {roomId} />{/if}

<div class="page">
  {#if error}
    <Empty icon="alert" tone="bad" title={denied ? "Invitation needed" : "Could not reach that room"} detail={error}>
      <Button href="/" icon="back">Home</Button>
    </Empty>
  {:else if snapshot && !snapshot.exists && snapshot.ended}
    <!--
      A ROOM THAT HAS FINISHED IS NOT A ROOM THAT HAS NOT STARTED, and this screen used
      to have only the second sentence. Both are `404 {exists:false}`, so a link to a
      room whose owner had left drew "Nobody is in tulip-otter-9682 yet" and a poll that
      would go on hoping every three seconds for as long as the tab was open.

      The owner's rule makes this the ordinary way an evening finishes — whoever started
      the room leaves and it ends for everybody — so it needs to read as an ending rather
      than as a fault. No "Copy the invite": that link cannot work again, for anybody.
    -->
    <Empty
      icon="people"
      tone="bad"
      title="That room has ended"
      detail="Whoever started {roomId} has left, so it closed for everyone. Room codes are not reused, so this link will not open again — start something and send a new one."
    >
      <Button href="/" variant="primary" icon="search">Find something</Button>
    </Empty>
  {:else if snapshot && !snapshot.exists}
    <Empty
      icon="people"
      title="Nobody is in {roomId} yet"
      detail="Pick something to start this room, then invite someone to watch with you."
    >
      <Button href="/" variant="primary" icon="search">Find something</Button>
      <Button onclick={share}>Invite</Button>
    </Empty>
  {:else if room}
    <div class="waiting">
      <p class="code"><Icon name="people" size={17} /> {room.name || roomId}</p>
      <h1>Waiting for the host</h1>
      <p class="detail">
        {count(room.members.length, 'person', 'people')} here. As soon as someone starts something,
        everyone lands in it — you do not need to do anything.
      </p>

      <ul class="people">
        {#each room.members as member (member.id)}
          <li>
            <span class="avatar" style:background={member.color}>{(member.name || '?').slice(0, 1).toUpperCase()}</span>
            <span class="who">
              <b>{member.name}</b>
            </span>
            <span class="since">{ago(member.joinedAt)}</span>
          </li>
        {/each}
      </ul>

      <div class="acts">
        <Button href="/" variant="primary" icon="search">Pick something</Button>
        <Button onclick={share} icon="link">Invite</Button>
      </div>
      <p class="poll"><Spinner size={13} /> Watching for a change…</p>
    </div>
  {:else}
    <div class="waiting">
      <p class="poll"><Spinner size={15} /> Looking for room {roomId}…</p>
    </div>
  {/if}
</div>

<style>
  .page {
    padding: 24px var(--page-pad) 48px;
  }
  .waiting {
    max-width: 46ch;
    margin-inline: auto;
    text-align: center;
    display: grid;
    justify-items: center;
    gap: 8px;
  }
  .code {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 12px;
    border-radius: 99px;
    border: 1px solid color-mix(in oklab, var(--accent) 45%, var(--line));
    background: color-mix(in oklab, var(--accent) 14%, transparent);
    color: var(--text);
    font-family: var(--mono);
    font-size: 0.8rem;
    font-weight: 600;
  }
  h1 {
    margin-top: 10px;
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .detail {
    color: var(--text-dim);
    font-size: 0.85rem;
    line-height: 1.55;
    text-wrap: pretty;
  }

  .people {
    list-style: none;
    margin: 18px 0 6px;
    padding: 0;
    width: 100%;
    text-align: left;
  }
  .people li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 4px;
  }
  .people li + li {
    border-top: 1px solid color-mix(in oklab, var(--line) 55%, transparent);
  }
  .avatar {
    display: grid;
    place-content: center;
    width: 28px;
    height: 28px;
    flex: none;
    border-radius: 50%;
    color: var(--bg);
    font-size: 0.76rem;
    font-weight: 700;
  }
  .who {
    flex: 1;
    min-width: 0;
    font-size: 0.86rem;
  }
  .since {
    color: var(--text-faint);
    font-size: 0.72rem;
  }

  .acts {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 14px;
  }
  .poll {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 14px;
    color: var(--text-faint);
    font-size: 0.76rem;
  }
</style>
